import * as service from "../common/service";
import { getCurrentApiBaseUrl, getV3ApiBaseUrl } from "../common/constants";
import moment from "moment";

// Dynamic API URL functions that get the current API base URL at runtime
const getDeviceApi = () => `${getCurrentApiBaseUrl()}/user/device`;
const getDevicePropertyAPI = () => `${getCurrentApiBaseUrl()}/user/deviceproperty`;
const getDevicePropertySetDetails = () => `${getCurrentApiBaseUrl()}/user/devicepropertyset`;
const getDeviceFirmware = () => `${getCurrentApiBaseUrl()}/user/firmware`;
const getDeviceAdvance = () => `${getCurrentApiBaseUrl()}/data/devicedata-advanced`;
const getDevicedata = () => `${getCurrentApiBaseUrl()}/data/devicedata`;
const getDeviceFirmwareDownload = () => `${getCurrentApiBaseUrl()}/user/firmware-media`;
const getStatSamplesQuery = () => `${getV3ApiBaseUrl()}/v3/stat-samples/query`;

const deviceService = {
  getDeviceList: (payload) => {
    let filter = "";
    if (payload && Object.keys(payload).length > 0) {
      filter = Object.keys(payload).reduce(
        (acc, val, index) =>
          Object.keys(payload).length - 1 === index
            ? `${acc}${val}=${payload[val]}`
            : `${acc}${val}=${payload[val]}&`,
        "?"
      );
    }
    return service.get(`${getDeviceApi()}${filter}`, {});
  },
  getDevicePropertyList: (payload) => {
    return service.get(`${getDevicePropertyAPI()}?deviceDataModelId=${payload}`, {});
  },
  getDevicePropertySetList: (payload) => {
    return service.get(`${getDevicePropertySetDetails()}?deviceDataModelId=${payload}`, {});
  },
  getDeviceDetailById: (payload) => {
    return service.get(`${getDeviceApi()}/${payload}`);
  },
  getDevicePropertyDetails: (payload) => {
    return service.get(`${getDevicePropertySetDetails()}?deviceDataModelId=${payload}`);
  },
  addDevice: (payload) => {
    return service.postJson(getDeviceApi(), payload);
  },
  deleteDevice: (payload) => {
    return service.deleteJson(`${getDeviceApi()}?deviceDataModelId=${payload.deviceId}`);
  },
  updateDevice: (payload) => {
    return service.putJson(getDeviceApi(), payload);
  },
  addPropertySet: (payload) => {
    return service.postJson(
      `${getDevicePropertySetDetails()}`,
      payload.propertyInfo
    );
  },
  deletePropertySet: (payload) => {
    return service.deleteJson(
      `${getDevicePropertySetDetails()}?devicePropertySetId=${payload.devicePropertySetId}`
    );
  },
  getPropertySetDetailsById: (payload) => {
    return service.get(`${getDevicePropertySetDetails()}/${payload.propertyId}`);
  },
  updatePropertySet: (payload) => {
    return service.putJson(`${getDevicePropertySetDetails()}`, payload.propertyInfo);
  },
  getPropertyDetailsById: (payload) => {
    return service.get(`${getDevicePropertyAPI()}/${payload}`);
  },
  updateProperty: (payload) => {
    return service.putJson(`${getDevicePropertyAPI()}`, payload);
  },
  createProperty: (payload) => {
    return service.postJson(`${getDevicePropertyAPI()}`, payload);
  },
  deleteProperty: (payload) => {
    return service.deleteJson(
      `${getDevicePropertyAPI()}?devicePropertyId=${payload.devicePropertyId}`
    );
  },
  getDeviceFirmwareList: (payload) => {
    let filter = "";
    if (payload && Object.keys(payload).length > 0) {
      filter = Object.keys(payload).reduce(
        (acc, val, index) =>
          Object.keys(payload).length - 1 === index
            ? `${acc}${val}=${payload[val]}`
            : `${acc}${val}=${payload[val]}&`,
        "?"
      );
    }
    return service.get(`${getDeviceFirmware()}${filter}`);
  },
  getFirmwareDetailsById: (payload) => {
    return service.get(`${getDeviceFirmware()}/${payload.firmwareId}`);
  },
  updateFirmwareDetailsById: ({ userInfo }) => {
    return service.putJson(getDeviceFirmware(), userInfo);
  },
  createFirmwareDetailsById: ({ userInfo }) => {
    const data = new FormData();
    data.append("deviceId", userInfo.deviceDataModelId);
    data.append("name", userInfo.name);
    data.append("displayVersion", userInfo.displayVersion);
    data.append("description", userInfo.description);
    data.append("fileHash", userInfo.fileHash);
    data.append("file", userInfo.file);
    return service.postMultipart(getDeviceFirmware(), data);
  },
  deleteFirmwareDetailsById: (payload) => {
    return service.deleteJson(
      `${getDeviceFirmware()}?firmwareId=${payload.firmwareId}`
    );
  },
  checkIfSensorExist: (payload) => {
    return service.postVersionJson(
      `${getDeviceAdvance()}/?pageSize=1&pageNumber=0&sortOrder=DESC`,
      payload
    );
  },
  getDeviceDataFromTimeStampList: (payload) => {
    const { sensorsMacAddress, ...restPayload } = payload;
    return service.postJson(
      `${getDeviceAdvance()}?sortBy=data.Timestamp.value&sortOrder=DESC`,
      restPayload
    );
  },
  getDeviceDataListFromDevTimeStamp: (payload) => {
    return service.postJson(
      `${getDeviceAdvance()}?sortBy=data.TimestampD.value&sortOrder=DESC`,
      payload
    );
  },
  fetchUserDeviceData: (payload) => {
    return service.postJson(
      `${getDeviceAdvance()}?sortBy=data.Timestamp.value&sortOrder=DESC`,
      payload
    );
  },
  fetchUserDeviceAggregateData: (payload, filterData = '') => {
    let filter = "";
    if (filterData && Object.keys(filterData).length > 0) {
      filter = Object.keys(filterData).reduce(
        (acc, val, index) =>
          Object.keys(filterData).length - 1 === index
            ? `${acc}${val}=${filterData[val]}`
            : `${acc}${val}=${filterData[val]}&`,
        "?"
      );
    } else {
      filter = "?sortBy=data.Timestamp.value&sortOrder=DESC";
    }
    return service.postVersionJson(
      `${getDevicedata()}/aggregate${filter}`,
      payload
    );
  },
  fetchUserFirstPageData: (payload) => {
    return service.postJson(
      `${getDeviceAdvance()}?sortBy=data.Timestamp.value&sortOrder=DESC&pageSize=5&pageNumber=0`,
      payload
    );
  },
  fetchTableDeviceData: ({ payload, data }) => {
    let filter = "";
    if (payload && Object.keys(payload).length > 0) {
      filter = Object.keys(payload).reduce(
        (acc, val, index) =>
          Object.keys(payload).length - 1 === index
            ? `${acc}${val}=${payload[val]}`
            : `${acc}${val}=${payload[val]}&`,
        "?"
      );
    }
    return service.postJson(`${getDeviceAdvance()}/owner${filter}`, data);
  },
  fetchStatSamples: ({ deviceIds = [], from, to } = {}) => {
    const body = { deviceIds };
    if (from) {
      body.from = from;
    }
    if (to) {
      body.to = to;
    }
    return service.postVersionJson(getStatSamplesQuery(), body, "json");
  },
  createDeviceData: (payload) => {
    return service.postJson(getDevicedata(), payload);
  },
  deleteDeviceData: (payload) => {
    return service.postJson(
      `${getCurrentApiBaseUrl()}/data/${payload.deviceDataModelId}/devicedata/delete`,
      {
        deviceDataIds: [payload.deviceDataId],
      }
    );
  },
  deleteMultipleDeviceData: (payload) => {
    let finalObj = {};
    finalObj.deviceCriteria = [
      {
        key: "ErrorLog",
        operator: "IsSet",
        value: null,
        valueFrom: null,
        valueTo: null,
        isDateValue: false,
      },
    ];
    if (payload.userIds) {
      finalObj.userIds = payload.userIds;
    } else {
      finalObj.deviceDataIds = payload.deviceDataIds;
    }
    return service.postJson(
      `${getCurrentApiBaseUrl()}/data/${payload.deviceDataModelId}/devicedata/delete`,
      finalObj
    );
  },
  deleteMultipleAlertDeviceData: (payload) => {
    let finalObj = {};
    if (payload.userIds) {
      finalObj.rangeEndDateTime = moment().utc().format("YYYY-MM-DDTHH:mm:ss");
      finalObj.rangeStartDateTime = moment().subtract(5, 'year').utc().format("YYYY-MM-DDTHH:mm:ss");
      finalObj.userIds = payload.userIds;
    } else {
      finalObj.deviceCriteria = [
        {
          key: "ErrorLog",
          operator: "IsSet",
          value: null,
          valueFrom: null,
          valueTo: null,
          isDateValue: false,
        },
      ];
      finalObj.deviceDataIds = payload.deviceDataIds;
    }
    return service.postJson(
      `${getCurrentApiBaseUrl()}/data/${payload.deviceDataModelId}/devicedata/delete`,
      finalObj
    );
  },
  downloadFirmwareVersion: (payload) => {
    return service.downloadFirmware(`${getDeviceFirmwareDownload()}/${payload}`);
  },
  updateDeviceData: (payload) => {
    return service.postVersionJson(
      getDevicedata(),
      payload
    );
  },
  fetchUserDeviceDataFromTable: (tableId, rowId) => {
    return service.get3Version(`${getDevicedata()}/${tableId}/${rowId}`);
  },
  fetchSavedDiagnosticPanelData: (payload, filterData) => {
    let filter = "?pageNumber=0&pageSize=1&sortBy=minValueProvidedOn&sortOrder=DESC";
    if (filterData && Object.keys(filterData).length > 0) {
      filter = Object.keys(filterData).reduce(
        (acc, val, index) =>
          Object.keys(filterData).length - 1 === index
            ? `${acc}${val}=${filterData[val]}`
            : `${acc}${val}=${filterData[val]}&`,
        "?"
      );
    }
    return service.postVersionJson(
      `${getDeviceAdvance()}/owner/custom-criteria${filter}`,
      payload
    );
  }
};

export default deviceService;
